import { formatMetricValue } from './formatters';
import type { MetricFormat } from './formatters';

/**
 * How each row of the report's Key Performance Metrics card is written.
 *
 * The backend sends raw floats (CAGR as 0.057, Sharpe as 0.3937); this list
 * decides which are percentages. Anything not listed is a plain ratio.
 *
 * Two places show these figures: the report's own KPM card and the stat tiles
 * on Soteria's "View report" card under a reply. Both read this list, so they
 * cannot disagree. When the tiles rounded the numbers themselves, an advisor
 * saw "CAGR% 0.06" in the chat beside 5.70% on the report.
 */
const KPM_METRIC_FORMAT: Record<string, MetricFormat> = {
  'Risk-Free Rate': 'percent',
  'Time in Market': 'percent',
  'Cumulative Return': 'percent',
  'CAGR%': 'percent',
  'Prob. Sharpe Ratio': 'percent',
  'Volatility (ann.)': 'percent',
  'Daily Value-at-Risk': 'percent',
  'Expected Shortfall (cVaR)': 'percent',
  Alpha: 'percent',
  'Upside Capture': 'percent',
  'Downside Capture': 'percent',
  'Kelly Criterion': 'percent',
  'Win Days': 'percent',
  'Win Month': 'percent',
  'Win Quarter': 'percent',
  'Win Year': 'percent',
  'Avg. Up Month': 'percent',
  'Avg. Down Month': 'percent',
  'Max Consecutive Wins': 'int',
  'Max Consecutive Losses': 'int',
};

/** The display format of a KPM row, by its label. */
export const kpmFormat = (label: string): MetricFormat =>
  KPM_METRIC_FORMAT[label] ?? 'ratio';

/** A KPM value written as the report writes it: 0.057 under "CAGR%" is 5.70%. */
export const formatKpmValue = (
  value: number | string | null | undefined,
  label: string,
): string => formatMetricValue(value, kpmFormat(label));
